import React, { useState, useEffect, useRef } from 'react';
import styles from './Chatbot.module.css';
import EmojiPicker from './EmojiPicker';
import { generateAIResponse } from './openRouterApi';
import { supabase } from '../../lib/supabaseClient';

// Custom chatbot configuration

const ChatbotComponent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showWelcomeNotification, setShowWelcomeNotification] = useState(false);
  const [isNotificationHiding, setIsNotificationHiding] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', mobile: '' });
  const [formErrors, setFormErrors] = useState({});
  const [servicesSent, setServicesSent] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);
  const chatBodyRef = useRef(null);

  // Smooth scrolling to the latest message
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  // Get current hour for time-based greetings
  const getCurrentTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Predefined Q&A pairs
  const QA_PAIRS = {
    // Service Information
    "what is demand generation": "Demand generation is a marketing strategy focused on creating awareness and interest in your company's products or services through targeted campaigns. The goal is to attract, nurture, and convert leads into qualified opportunities.",
    "what industries do you work with": "We work across a wide range of industries including Technology, SaaS, Telecom, Finance, Healthcare, Manufacturing, and more.",
    "how do i get started": "You can book a discovery call with our team or request a media kit. From there, we understand your ICP (Ideal Customer Profile), goals, and suggest a tailored campaign.",
    "what services do you offer under demand generation": "Our services include:\n• Content Syndication (Email & Phone)\n• Multi-touch Campaigns (PQ/CQ)\n• Webinar Registrations\n• Opt-in/Double Opt-in Campaigns\n• BANT, MQL, HQL, SRL Lead Generation\n• Data Appending & List Rentals\n• Survey and Market Research\n• Account-Based Marketing (ABM)\n• Data Cleansing & Validation",
    "do you offer b2b or b2c services": "We specialize in B2B lead and demand generation.",
    "how do you qualify leads": "Leads are qualified based on your parameters — such as job title, company size, industry, geography, and buying intent. We also use frameworks like BANT, MQL, and HQL.",
    "can we run a pilot": "Yes, we offer test campaigns to demonstrate ROI and effectiveness before scaling up.",
    "are you gdpr compliant": "Yes, we are fully GDPR compliant and ISO 27001:2013 & ISO 27701:2019 certified. All leads are sourced and qualified following strict data privacy regulations.",
    "where do you source your data": "We have access to over 300M+ B2B contacts globally through strategic data partnerships, verified databases, and opt-in procedures.",
    "can you guarantee lead quality": "Yes, we offer guaranteed replacement for leads that don't meet the agreed-upon criteria. We also provide 100% call recordings and transcripts upon request.",
    "what roi can we expect": "ROI varies depending on the campaign type and audience. However, our average clients see a reduction in lead acquisition costs by 30-40% and improved conversion rates.",
    "how long for results": "Typically within 2-4 weeks from the campaign start date, depending on the complexity and targeting parameters.",
    "do you provide reporting": "Yes, we share weekly performance dashboards, lead status reports, and campaign insights in real-time or on demand.",
    "what is your pricing model": "We offer flexible models — CPL (Cost per Lead), CPM (Cost per Thousand Impressions), and Project-based pricing depending on your campaign type.",
    "is there a minimum commitment": "No long-term commitment is required. We also offer short pilot campaigns to start with.",
    "can you customize campaigns": "Absolutely! All campaigns are tailored to match your ideal customer profile, geographies, and sales goals.",
    "what are the next steps": "We'll schedule a brief discovery call, define campaign KPIs, and present a custom proposal.",
    "how big is your database": "We have access to over 300 million+ B2B contacts globally, spanning various industries, job functions, and geographies.",
    "what data do you collect": "We collect firmographic (company size, revenue, industry), technographic (tools used), and demographic (job title, seniority, location) data to target precisely.",
    "when was b2bindemand founded": "B2BinDemand was founded in 2013 and has since been a pioneer in B2B lead generation and demand generation services.",
    "how long have you been in business": "B2BinDemand has been serving clients since our founding in 2013, bringing over a decade of expertise in B2B lead generation.",
    "can i target specific job titles": "Yes, we customize every campaign to target your ideal customer profile (ICP) including job roles, industries, regions, and company size.",
    "do you provide intent data": "Yes, we leverage intent data engines to identify prospects actively researching topics relevant to your product/service.",
    "how do you ensure data accuracy": "We run regular data cleansing and validation cycles and only use verified sources. All contacts are manually verified before delivery.",
    "what is your lead generation process": "1. Define Target Audience & KPIs\n2. Build/Validate Custom Database\n3. Execute Campaign (via Email, Phone, or Both)\n4. Qualify Leads via Custom Criteria\n5. Deliver Leads with Notes, Call Recordings & Transcripts\n6. Weekly Reporting and Optimization",
    "how do you generate leads": "We use multi-channel campaigns, including email outreach, cold calling, and content syndication via business phones.",
    "do you support abm": "Yes, we offer Account-Based Marketing (ABM) campaigns where we hyper-target your named accounts.",
    "do you provide scripts": "We provide both: our team can develop scripts based on your campaign objectives, or work with your existing assets.",
    "what is your turnaround time": "After agreement, we typically take 3–5 business days to go live with a fully custom campaign.",
    "how do you ensure quality": "All leads go through manual and automated quality checks, including validation against your ICP, real-time data verification, and internal quality audits.",
    "can you provide recordings": "Yes, we offer 100% call recordings and transcripts on request for full transparency and auditing.",
    "what if leads dont meet criteria": "We offer free lead replacement for any invalid or misaligned leads as per the agreed SLA.",
    "do you verify leads": "Absolutely. Every lead is double verified by our QA team for accuracy, relevance, and qualification standards.",
    "are the leads opt in": "Yes. We follow Opt-In or Double Opt-In processes depending on the campaign and your compliance requirements.",
    "are you ccpa compliant": "Yes, we are fully GDPR & CCPA compliant, and ISO 27001:2013 & 27701:2019 certified.",
    // Basic greetings
    'hi': `Hi! 👋 How can I assist you today?`,
    'hello': `Hello! 👋 How may I help you today?`,
    'hey': `Hey there! 👋 How can I be of assistance?`,
    'good morning': `${getCurrentTimeGreeting()}! 🌅 How can I help you today?`,
    'good afternoon': `${getCurrentTimeGreeting()}! ☀️ How can I assist you?`,
    'good evening': `${getCurrentTimeGreeting()}! 🌆 How may I help you?`,
    'how are you': 'I\'m doing great, thank you for asking! 😊 How can I assist you today?',
    'how are you doing': 'I\'m doing well, thanks for asking! 😊 What can I help you with?',

    // Common responses
    'thanks': 'You\'re welcome! 😊 Is there anything else I can help you with?',
    'thank you': 'You\'re welcome! 😊 Is there anything else I can help you with?',
    'thx': 'You\'re welcome! 😊 Need help with anything else?',
    'welcome': 'Thank you! 😊 How can I assist you today?',
    'ok': 'Great! 😄 What would you like to know more about?',
    'okay': 'Great! 😄 What would you like to know more about?',
    'ok sure': 'Excellent! 😄 What would you like to explore first?',
    'sure': 'Wonderful! 😄 What would you like to know?',
    'no': 'Alright! 😊 Feel free to ask if you need anything else.',
    'no thanks': 'You\'re welcome! 😊 Feel free to reach out if you need anything in the future.',
    'nope': 'Alright! 😊 Don\'t hesitate to ask if you need help later.',
    'bye': 'Goodbye! 👋 Have a great day! Feel free to return if you need any assistance.',
    'goodbye': 'Goodbye! 👋 Have a great day! Don\'t hesitate to reach out if you need help in the future.',
    'see you': 'See you! 👋 Have a wonderful day! Feel free to chat again anytime.',
    
    // Company information
    'what is b2bindemand': 'B2BinDemand is a leading B2B solutions provider founded in 2013. We specialize in lead generation, digital marketing, and sales enablement services. With ISO 27701:2019 & ISO 27001:2013 certifications, we deliver proven Go-to-Market strategies designed to drive real results. Would you like to know more about our specific services?',
    'when was b2bindemand founded': 'B2BinDemand was founded in 2013 in Wilmington, Delaware. Since then, we\'ve grown to a team of 170+ experts delivering exceptional B2B solutions. Would you like to learn more about our journey?',
    'where is b2bindemand located': 'Our headquarters is located in Wilmington, Delaware. From here, our team of 170+ experts serves clients globally. Would you like to get in touch with us?',
    'what certifications do you have': 'We are proud to be certified with ISO 27701:2019 & ISO 27001:2013, demonstrating our commitment to data security and quality management. These certifications ensure we deliver the highest standards of service to our clients.',
    'what is your team size': 'We have a dedicated team of 170+ experts and creative partners who specialize in crafting high-performing B2B campaigns. Our specialists cover everything from lead generation to market research.',
    'what services do you offer': 'We offer comprehensive B2B services including:\n• Content Syndication\n• Account Based Marketing (ABM)\n• Appointment Setting\n• Market Research\n• Customized Recruitment Solutions\n• White Paper Syndication\n• Event Registration\n• Data Research\n• Demand Generation\n• Content Marketing\n• Email Marketing\n• Go-to-Market (GTM) Strategies\nWhich of these services interests you?',
    'how can you help my business': 'We can help grow your business through our specialized B2B services and proven Go-to-Market strategies. Our team of 170+ experts excels in:\n• Maximizing your ROI\n• Building lasting business relationships\n• Generating high-quality leads\n• Creating targeted marketing campaigns\n• Providing market insights\nWould you like to explore any specific service?',
    'tell me about your company': 'B2BinDemand is a leading B2B solutions provider established in 2013 in Wilmington, DE. We have a team of 170+ experts and are ISO 27701:2019 & ISO 27001:2013 certified. We specialize in taking B2B sales and marketing to an unmatched level through proven Go-to-Market strategies. Would you like to know about our specific services?',
    'what is your experience': 'Since 2013, we\'ve been delivering exceptional B2B solutions. Our team of 170+ experts has helped numerous businesses grow through our comprehensive services. We\'re ISO 27701:2019 & ISO 27001:2013 certified and specialize in everything from Content Syndication to Account Based Marketing. Would you like to see our services?',
    'how do i contact you': 'You can reach us through multiple channels:\n• Phone: +1 302-966-9297\n• Email: contact@b2bindemand.com\n• Website: www.b2bindemand.com\nWould you like to provide your contact information for a personalized consultation?',
    'what is your phone number': 'You can reach us at +1 302-966-9297. Would you like to schedule a call with our team?',
    'what is your email': 'You can email us at contact@b2bindemand.com. Would you like to provide your contact information for a personalized response?',
    'what is your website': 'Our website is www.b2bindemand.com. You can find detailed information about our services there. Would you like me to tell you about any specific service?',
    'i need help': 'I\'d be happy to help! We offer comprehensive B2B solutions including lead generation, ABM, market research, and more. Let me show you our services that might address your needs:',
    'pricing': 'I can help you get detailed pricing information customized to your business needs. Our solutions are tailored to maximize your ROI. Please provide your contact details and our team will get back to you with a customized quote:'
  };

  const SERVICES = [
    { 
      text: 'B2B Lead Generation', 
      value: 'B2B Lead Generation', 
      action: 'openForm',
      icon: '📊'
    },
    { 
      text: 'Digital Marketing Solutions', 
      value: 'Digital Marketing Solutions', 
      action: 'openForm',
      icon: '🚀'
    },
    { 
      text: 'Sales Enablement', 
      value: 'Sales Enablement', 
      action: 'openForm',
      icon: '💼'
    },
    { 
      text: 'Custom CRM Integration', 
      value: 'Custom CRM Integration', 
      action: 'openForm',
      icon: '🔄'
    }
  ];

  useEffect(() => {
    // Initial welcome message
    const initialMessage = {
      message: `Hi! 👋 I'm Arjun, your B2B assistant. How can I help you today?`,
      type: 'bot',
      id: Date.now(),
      source: 'chat'
    };
    setMessages([initialMessage]);

    // Show welcome notification after 5 seconds if chatbot is not open
    const timer = setTimeout(() => {
      if (!isOpen) {
        setShowWelcomeNotification(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [isOpen]);

  // Typing Animation Component
  const TypingIndicator = () => (
    <div className="flex items-start gap-2">
      <div className="w-6 h-6 rounded-full bg-[#005f73]/10 flex items-center justify-center flex-shrink-0">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-4 h-4 text-[#005f73]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      </div>
      <div className="max-w-[75%] rounded-2xl px-4 py-2 text-sm break-words bg-white shadow-sm border border-gray-100">
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.3s]"></div>
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.15s]"></div>
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"></div>
        </div>
      </div>
    </div>
  );



  const handleCustomMessage = async (message) => {
    const normalizedMessage = message.toLowerCase().trim();
    
    // Check for exact matches first
    if (QA_PAIRS[normalizedMessage]) {
      return QA_PAIRS[normalizedMessage];
    }

    // Check for greetings and common responses
    const greetings = ['hi', 'hello', 'hey', 'greetings', 'good morning', 'good afternoon', 'good evening'];
    if (greetings.some(greeting => normalizedMessage.includes(greeting))) {
      if (normalizedMessage.includes('morning')) return QA_PAIRS['good morning'];
      if (normalizedMessage.includes('afternoon')) return QA_PAIRS['good afternoon'];
      if (normalizedMessage.includes('evening')) return QA_PAIRS['good evening'];
      return QA_PAIRS['hi'];
    }

    // Check for partial matches in QA_PAIRS
    for (const [key, value] of Object.entries(QA_PAIRS)) {
      if (normalizedMessage.includes(key)) {
        // Show contact form for specific queries
        if (key.includes('contact') || key.includes('start') || 
            key.includes('pricing') || key.includes('pilot') || 
            key.includes('next steps')) {
          setShowForm(true);
        }
        return value;
      }
    }

    try {
      // If no predefined answer, use OpenRouter AI with enhanced context
      const systemPrompt = `You are a B2B demand generation expert assistant for B2BinDemand. Help with questions about lead generation, marketing, and related topics.

Key information:
- We specialize in B2B lead generation and demand generation
- Our services: content syndication, ABM, lead qualification, data services
- We have 300M+ B2B contacts globally
- We are GDPR and CCPA compliant
- We offer lead quality guarantee
- For pricing/sales inquiries, recommend contacting sales@b2bindemand.com

Keep responses professional and concise. Focus on our B2B services.`;

      const aiResponse = await generateAIResponse([
        { type: 'system', text: systemPrompt },
        { type: 'user', text: message }
      ]);

      // Check if response suggests contacting sales
      if (aiResponse.toLowerCase().includes('contact') || 
          aiResponse.toLowerCase().includes('schedule') || 
          aiResponse.toLowerCase().includes('sales team') || 
          aiResponse.toLowerCase().includes('consultation')) {
        setShowForm(true);
      }

      return aiResponse;
    } catch (error) {
      console.error('Error getting AI response:', error);
      setShowForm(true);
      return "I apologize, but I'm having trouble connecting to our AI service. Would you like to speak with our team directly? You can reach us at sales@b2bindemand.com.";
    }
  };

  const handleSubmit = async () => {
    const message = inputMessage.trim();
    if (!message || isLoading) return;

    // Add user message
    const userMessage = {
      message: message,
      type: 'user',
      id: Date.now(),
      source: 'chat'
    };

    // Clear input and update states
    setInputMessage('');
    setIsTyping(true);
    setIsLoading(true);
    inputRef.current?.focus();

    // Add user message
    setMessages(prev => [...prev, userMessage]);

    try {
      // Get bot response
      const botResponse = await handleCustomMessage(message);

      // Add bot's response
      setMessages(prev => [...prev, {
        message: botResponse,
        type: 'bot',
        id: Date.now(),
        source: 'chat'
      }]);

      // Show services if the response indicates services should be shown
      if (botResponse.toLowerCase().includes('services') || 
          botResponse.toLowerCase().includes('solutions') || 
          botResponse.toLowerCase().includes('offerings')) {
        setServicesSent(true);
        setMessages(prev => [...prev, {
          message: '',
          type: 'bot',
          id: Date.now() + 1,
          showServices: true
        }]);
      }

      // Show contact form if the response suggests contact or pricing
      if (botResponse.toLowerCase().includes('contact') || 
          botResponse.toLowerCase().includes('schedule') || 
          botResponse.toLowerCase().includes('pricing')) {
        setShowForm(true);
        setMessages(prev => [...prev, {
          message: '',
          type: 'bot',
          id: Date.now() + 2,
          showForm: true
        }]);
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      setMessages(prev => [...prev, {
        message: "I apologize, but I'm having trouble connecting to our AI service. Would you like to speak with our team directly?",
        type: 'bot',
        id: Date.now(),
        source: 'chat'
      }]);
      setShowForm(true);
    }

    setIsTyping(false);
    setIsLoading(false);
  };

  const handleOptionClick = (value) => {
    // Add user's selection as a message
    setMessages(prev => [...prev, {
      message: value,
      type: 'user',
      id: Date.now(),
      source: 'chat'
    }]);

    // Show form for any option
    setShowForm(true);
    // Add form directly without extra message
    setMessages(prev => [...prev, {
      message: '',
      type: 'bot',
      id: Date.now(),
      showForm: true
    }]);
  };

  const handleFormInput = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Basic validation
    const errors = {};
    if (field === 'email' && !/\S+@\S+\.\S+/.test(value)) {
      errors.email = 'Invalid email format';
    }
    if (field === 'mobile' && !/^\d{10}$/.test(value)) {
      errors.mobile = 'Mobile must be 10 digits';
    }
    setFormErrors(errors);
  };

  const handleContactSubmit = async () => {
    // Validate before submitting
    const errors = {};
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!/^\d{10}$/.test(formData.mobile)) {
      errors.mobile = 'Mobile must be 10 digits';
    }

    if (Object.keys(errors).length === 0) {
      try {
        // Insert data into Supabase
        const { error } = await supabase
          .from('chatbot_leads')
          .insert([
            {
              email: formData.email,
              phone: formData.mobile,
              created_at: new Date().toISOString(),
              source: 'chatbot'
            }
          ]);

        if (error) throw error;

        // Submit contact information
        setShowForm(false);
        setFormData({ email: '', mobile: '' });
        setFormErrors({});

        // Add thank you message to chat
        setMessages(prev => [...prev, {
          message: 'Thank you for providing your contact information! 🎉\n\nOur team will get back to you shortly. In the meantime, feel free to ask me any other questions about our services.',
          type: 'bot',
          id: Date.now(),
          source: 'chat'
        }]);

        // Show typing indicator briefly for natural feel
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 500);
      } catch (error) {
        console.error('Error submitting form:', error);
        setMessages(prev => [...prev, {
          message: 'Sorry, there was an error submitting your information. Please try again.',
          type: 'bot',
          id: Date.now(),
          source: 'chat'
        }]);
      }
    } else {
      setFormErrors(errors);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading && !isTyping) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  const toggleChatbot = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (newIsOpen) {
      setShowWelcomeNotification(false);
      // Focus input after a small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const hideNotification = () => {
    setIsNotificationHiding(true);
    setTimeout(() => {
      setShowWelcomeNotification(false);
      setIsNotificationHiding(false);
    }, 300); // Match animation duration
  };

  const toggleVoice = () => {
    alert('Voice input is currently unavailable.');
  };

  const renderMessage = (message) => {
    return (
      <div
        className={`flex items-start gap-2 ${message.type === 'user' ? 'flex-row-reverse' : ''}`}
        key={message.id}
      >
        {message.type === 'bot' && (
          <div className="w-6 h-6 rounded-full bg-[#005f73]/10 flex items-center justify-center flex-shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 text-[#005f73]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
          </div>
        )}
        <div 
          className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm break-words ${message.type === 'user' 
            ? 'bg-[#005f73] text-white' 
            : 'bg-white shadow-sm border border-gray-100'}`}
        >
          {message.message && message.message.split('\n').map((line, i) => (
            <p key={i} className="mb-1 last:mb-0">{line}</p>
          ))}
          {message.showForm && showForm && (
            <div className="mt-4 space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => handleFormInput('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005f73] focus:border-transparent"
                />
                {formErrors.email && <div className="text-red-500 text-xs mt-1">{formErrors.email}</div>}
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Enter your mobile (10 digits)"
                  value={formData.mobile}
                  onChange={(e) => handleFormInput('mobile', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005f73] focus:border-transparent"
                  maxLength={10}
                />
                {formErrors.mobile && <div className="text-red-500 text-xs mt-1">{formErrors.mobile}</div>}
              </div>
              <button 
                onClick={handleContactSubmit}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${!formData.email || !formData.mobile 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-[#005f73] hover:bg-[#004f63] text-white'}`}
                disabled={!formData.email || !formData.mobile}
              >
                Submit
              </button>
            </div>
          )}
          {message.showServices && (
            <div className="mt-4 grid gap-3">
              {SERVICES.map((service, index) => (
                <button
                  key={index}
                  className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-[#005f73] transition-colors duration-200 text-left w-full"
                  onClick={() => {
                    setShowForm(true);
                    setMessages(prev => [...prev, {
                      message: `I'm interested in ${service.text}. Please help me get started.`,
                      type: 'user',
                      id: Date.now(),
                      source: 'chat'
                    }, {
                      message: 'I will help you learn more about our ' + service.text + '. Please provide your contact details:',
                      type: 'bot',
                      id: Date.now() + 1,
                      showForm: true
                    }]);
                  }}
                >
                  <span className="text-xl">{service.icon}</span>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{service.text}</h4>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </div>
                  <span className="text-[#005f73]">→</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.chatbotWrapper}>
      {showWelcomeNotification && (
        <div className={`${styles.welcomeNotification} ${isNotificationHiding ? styles.hide : ''}`}>
          <div className={styles.welcomeNotificationContent}>
            <p className={styles.welcomeNotificationText}>
              Hi! 👋 I'm Arjun, your B2B assistant. How can I help you today?
            </p>
          </div>
          <button onClick={hideNotification} className={styles.welcomeNotificationClose}>×</button>
        </div>
      )}
      <button
        onClick={toggleChatbot}
        className={styles.chatbotButton}
        aria-label="Toggle chat"
      >
        {showWelcomeNotification && !isOpen && <div className={styles.notificationBadge}>1</div>}
        {isOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
        )}
      </button>
      {isOpen && (
        <div className="fixed bottom-4 z-[9999] right-4 sm:right-6 md:right-8 lg:right-10 w-[calc(100%-2rem)] max-w-[320px] sm:max-w-[360px] md:max-w-[380px] lg:max-w-[400px] h-[85vh] max-h-[500px] bg-white rounded-xl shadow-lg flex flex-col overflow-hidden z-50 transition-all duration-300 ease-in-out">
          <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-[#005f73] to-[#005f73] text-white">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                  <line x1="9" y1="9" x2="9.01" y2="9" />
                  <line x1="15" y1="9" x2="15.01" y2="9" />
                </svg>
              </div>
              <span className="text-sm sm:text-base font-medium">B2BinDemand Support</span>
              {isLoading && <div className="animate-pulse">...</div>}
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setMessages([])} 
                className="text-xs sm:text-sm px-2 py-1 hover:bg-white/10 rounded transition-colors duration-200"
              >
                Clear
              </button>
              <button 
                onClick={toggleChatbot} 
                className="text-xl hover:text-gray-200 transition-colors duration-200 px-1"
              >
                ×
              </button>
            </div>
          </div>

          <div ref={chatBodyRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg) => renderMessage(msg))}
            {isTyping && <TypingIndicator />}
          </div>

          <div className="p-3 bg-white border-t border-gray-200">
            <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 bg-transparent border-0 focus:ring-0 text-sm sm:text-base outline-none"
                disabled={isLoading}
              />
              <div className="flex items-center space-x-1">
                <button
                  onClick={toggleVoice}
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors duration-200"
                  type="button"
                  disabled={isLoading}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-gray-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                </button>
                <button
                  onClick={handleSubmit}
                  className={`p-2 ${inputMessage.trim() && !isLoading && !isTyping ? 'bg-[#005f73] hover:bg-[#004f63]' : 'bg-gray-300'} rounded-full transition-colors duration-200`}
                  type="button"
                  disabled={!inputMessage.trim() || isLoading || isTyping}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotComponent;
